import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Activity,
  Zap,
  Clock,
  RefreshCw,
  Search,
  Bot,
  Crosshair,
  Star,
  X,
  ShieldCheck,
  BarChart2,
} from 'lucide-react';
import { apiClient } from '../../api/client';
import { ASHARE_BILINGUAL } from '../../shared/constants/bilingualTitles';
import { Lifecycle5State } from '../../components/common/QuantitativeDataTable';
import { formatPrice, formatVolume } from '../../shared/domain/formatters';
import { compareDecimalStrings, getDecimalSign, parseDecimalString, toChartFloat } from '../../shared/domain/decimal';

export interface WatchlistAggregatedItem {
  symbol: string;
  name: string;
  source: 'DOCTOR_A' | 'AMBUSH_RADAR' | 'MANUAL';
  strategy_tag?: string;
  last_closed_price: string | null;
  change_pct: string;
  reference_price: string | null;
  reference_bias_pct: string | null;
  bar_close_time_utc: string | null;
  sparkline_1m: string[];
}

export interface WatchlistItemsResponse {
  market_session_state: 'OPEN' | 'CLOSED';
  fsm_readiness_state: 'loading' | 'fresh' | 'stale' | 'disconnected' | 'empty';
  as_of_utc: string;
  total_count: number;
  items: WatchlistAggregatedItem[];
}

export interface WatchlistCandleItem {
  timestamp: number;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
  amount: string;
  is_closed: boolean;
  source_provider?: string;
}

export interface WatchlistCandlesResponse {
  symbol: string;
  timeframe: string;
  readiness: string;
  total_bars: number;
  candles: WatchlistCandleItem[];
}

export interface AShareWatchlistViewProps {
  colorMode?: 'mainland' | 'international';
}

export const AShareWatchlistView: React.FC<AShareWatchlistViewProps> = ({
  colorMode = 'mainland',
}) => {
  const [activeTab, setActiveTab] = useState<'ALL' | 'DOCTOR_A' | 'AMBUSH_RADAR' | 'MANUAL'>('ALL');
  const [items, setItems] = useState<WatchlistAggregatedItem[]>([]);
  const [sessionState, setSessionState] = useState<'OPEN' | 'CLOSED'>('CLOSED');
  const [readinessState, setReadinessState] = useState<Lifecycle5State>('loading');
  const [asOfUtc, setAsOfUtc] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Drawer state for deep-dive candles
  const [selectedStock, setSelectedStock] = useState<WatchlistAggregatedItem | null>(null);
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('1m');
  const [drawerCandles, setDrawerCandles] = useState<WatchlistCandleItem[]>([]);
  const [drawerLoading, setDrawerLoading] = useState<boolean>(false);

  const fetchWatchlistItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get<WatchlistItemsResponse>(
        '/api/v1/market/a-share/watchlist/items',
        { params: { source: 'ALL', active_only: 'true' } }
      );
      setItems(res.items || []);
      setSessionState(res.market_session_state || 'CLOSED');
      setReadinessState(res.fsm_readiness_state || 'fresh');
      setAsOfUtc(res.as_of_utc || null);
    } catch (err) {
      setReadinessState('disconnected');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWatchlistItems();
  }, [fetchWatchlistItems]);

  // Fetch drawer candles when stock or timeframe changes
  const fetchDrawerCandles = useCallback(async (sym: string, tf: string) => {
    setDrawerLoading(true);
    try {
      const res = await apiClient.get<WatchlistCandlesResponse>(
        '/api/v1/market/a-share/watchlist/candles',
        { params: { symbol: sym, timeframe: tf, limit: 100 } }
      );
      setDrawerCandles(res.candles || []);
    } catch (err) {
      setDrawerCandles([]);
    } finally {
      setDrawerLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedStock) {
      fetchDrawerCandles(selectedStock.symbol, selectedTimeframe);
    }
  }, [selectedStock, selectedTimeframe, fetchDrawerCandles]);

  // Tab counts
  const tabCounts = useMemo(() => {
    return {
      ALL: items.length,
      DOCTOR_A: items.filter((i) => i.source === 'DOCTOR_A').length,
      AMBUSH_RADAR: items.filter((i) => i.source === 'AMBUSH_RADAR').length,
      MANUAL: items.filter((i) => i.source === 'MANUAL').length,
    };
  }, [items]);

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (activeTab !== 'ALL' && item.source !== activeTab) {
        return false;
      }
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const codeMatch = item.symbol.toLowerCase().includes(q);
        const nameMatch = item.name.toLowerCase().includes(q);
        const tagMatch = (item.strategy_tag || '').toLowerCase().includes(q);
        if (!codeMatch && !nameMatch && !tagMatch) return false;
      }
      return true;
    });
  }, [items, activeTab, searchQuery]);

  // Helper for mainland colors: red for up (+), green for down (-)
  const getPriceColor = (sign: number) => {
    if (sign > 0) {
      return colorMode === 'mainland' ? 'var(--color-bearish)' : 'var(--color-bullish)';
    } else if (sign < 0) {
      return colorMode === 'mainland' ? 'var(--color-bullish)' : 'var(--color-bearish)';
    }
    return 'var(--text-primary)';
  };

  // Render SVG Sparkline
  const renderSparkline = (prices: string[], strokeColor: string) => {
    if (!prices || prices.length < 2) {
      return <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>暂无走势</span>;
    }
    const numPrices = prices
      .map((p) => {
        try {
          return toChartFloat(parseDecimalString(p));
        } catch {
          return null;
        }
      })
      .filter((n): n is number => n !== null);
    if (numPrices.length < 2) return null;

    const min = Math.min(...numPrices);
    const max = Math.max(...numPrices);
    const range = max - min || 1;
    const w = 110;
    const h = 28;
    const pad = 2;

    const points = numPrices
      .map((p, i) => {
        const x = pad + (i / (numPrices.length - 1)) * (w - 2 * pad);
        const y = h - pad - ((p - min) / range) * (h - 2 * pad);
        return `${Math.round(x)},${Math.round(y)}`;
      })
      .join(' ');

    const lastX = w - pad;
    const lastY = h - pad - ((numPrices[numPrices.length - 1] - min) / range) * (h - 2 * pad);

    return (
      <svg width={w} height={h} style={{ overflow: 'visible', verticalAlign: 'middle' }}>
        <polyline
          fill="none"
          stroke={strokeColor}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          points={points}
        />
        <circle cx={lastX} cy={lastY} r="2.5" fill={strokeColor} />
      </svg>
    );
  };

  // Format UTC date to CST
  const formatUtcToCst = (utcStr: string | null) => {
    if (!utcStr) return '--';
    try {
      const d = new Date(utcStr);
      return d.toLocaleString('zh-CN', { hour12: false });
    } catch {
      return utcStr;
    }
  };

  return (
    <div className="view-container animate-fade-in" style={{ width: '100%', boxSizing: 'border-box' }}>
      {/* 1. Standardized TSO 2.0 View Header */}
      <div className="view-header">
        <div className="view-title-group bilingual-view-header">
          <div className="bilingual-header-top">
            <Zap size={18} style={{ color: 'var(--accent-cyan)' }} />
            <h2 className="bilingual-header-title-zh">{ASHARE_BILINGUAL.watchlist.zh}</h2>
            {/* Session State Badge */}
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: '4px',
                background: sessionState === 'CLOSED' ? 'var(--badge-neutral-bg)' : 'var(--badge-bullish-bg)',
                color: sessionState === 'CLOSED' ? 'var(--text-secondary)' : 'var(--color-emerald)',
                border: `1px solid ${sessionState === 'CLOSED' ? 'var(--badge-neutral-border)' : 'var(--badge-bullish-border)'}`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <Clock size={12} />
              {sessionState === 'CLOSED' ? '○ CLOSED 闭市冻结' : '● LIVE 交易中'}
            </span>
            {/* Readiness Badge */}
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: '4px',
                background: readinessState === 'fresh' ? 'var(--badge-bullish-bg)' : 'var(--badge-warning-bg)',
                color: readinessState === 'fresh' ? 'var(--color-emerald)' : 'var(--color-warning)',
                border: `1px solid ${readinessState === 'fresh' ? 'var(--badge-bullish-border)' : 'var(--badge-warning-border)'}`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <ShieldCheck size={12} />
              {readinessState === 'fresh' ? 'W ≥ 3.0s 因果合规' : readinessState.toUpperCase()}
            </span>
          </div>
          <span className="bilingual-header-subtitle-en">{ASHARE_BILINGUAL.watchlist.en}</span>
          <p className="view-subtitle" style={{ margin: '3px 0 0 0' }}>
            高频毫秒级重采样 · 纯 Decimal 精度 · 严禁虚假 Mock 插值 · 覆盖核心重点监控池
          </p>
        </div>

        <div className="view-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {asOfUtc && (
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'right' }}>
              <span>快照切片: </span>
              <span style={{ fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>
                {formatUtcToCst(asOfUtc)}
              </span>
            </div>
          )}
          <button
            onClick={fetchWatchlistItems}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 12px',
              borderRadius: '6px',
              background: 'var(--btn-primary-bg)',
              color: 'var(--btn-primary-text)',
              border: 'none',
              fontSize: '11px',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
            }}
          >
            <RefreshCw size={12} className={loading ? 'spin-animation' : ''} />
            {loading ? '同步中...' : '刷新看板'}
          </button>
        </div>
      </div>

      {/* 2. Control Bar (Source Tabs & Search) */}
      <div
        className="watchlist-control-bar"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {/* Source Filter Tabs */}
        <div
          style={{
            display: 'inline-flex',
            background: 'var(--bg-card)',
            padding: '4px',
            borderRadius: '10px',
            border: '1px solid var(--border-subtle)',
            gap: '4px',
          }}
        >
          <button
            onClick={() => setActiveTab('ALL')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '12px',
              fontWeight: activeTab === 'ALL' ? 600 : 500,
              background: activeTab === 'ALL' ? 'var(--color-cyan)' : 'transparent',
              color: activeTab === 'ALL' ? 'var(--text-inverse)' : 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <Activity size={13} />
            全部标的
            <span
              style={{
                fontSize: '11px',
                padding: '1px 5px',
                borderRadius: '8px',
                background: 'var(--tag-inactive-bg)',
                color: activeTab === 'ALL' ? 'var(--color-cyan)' : 'var(--text-muted)',
              }}
            >
              {tabCounts.ALL}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('DOCTOR_A')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '12px',
              fontWeight: activeTab === 'DOCTOR_A' ? 600 : 500,
              background: activeTab === 'DOCTOR_A' ? 'var(--color-cyan)' : 'transparent',
              color: activeTab === 'DOCTOR_A' ? 'var(--text-inverse)' : 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <Bot size={13} />
            Doctor-A 策略推荐
            <span
              style={{
                fontSize: '11px',
                padding: '1px 5px',
                borderRadius: '8px',
                background: 'var(--tag-inactive-bg)',
                color: activeTab === 'DOCTOR_A' ? 'var(--color-cyan)' : 'var(--text-muted)',
              }}
            >
              {tabCounts.DOCTOR_A}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('AMBUSH_RADAR')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '12px',
              fontWeight: activeTab === 'AMBUSH_RADAR' ? 600 : 500,
              background: activeTab === 'AMBUSH_RADAR' ? 'var(--color-cyan)' : 'transparent',
              color: activeTab === 'AMBUSH_RADAR' ? 'var(--text-inverse)' : 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <Crosshair size={13} />
            潜伏雷达精选
            <span
              style={{
                fontSize: '11px',
                padding: '1px 5px',
                borderRadius: '8px',
                background: 'var(--tag-inactive-bg)',
                color: activeTab === 'AMBUSH_RADAR' ? 'var(--color-cyan)' : 'var(--text-muted)',
              }}
            >
              {tabCounts.AMBUSH_RADAR}
            </span>
          </button>

          <button
            onClick={() => setActiveTab('MANUAL')}
            style={{
              padding: '6px 14px',
              borderRadius: '6px',
              border: 'none',
              fontSize: '12px',
              fontWeight: activeTab === 'MANUAL' ? 600 : 500,
              background: activeTab === 'MANUAL' ? 'var(--color-cyan)' : 'transparent',
              color: activeTab === 'MANUAL' ? 'var(--text-inverse)' : 'var(--text-secondary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.15s ease',
            }}
          >
            <Star size={13} />
            交易员核心自选
            <span
              style={{
                fontSize: '11px',
                padding: '1px 5px',
                borderRadius: '8px',
                background: 'var(--tag-inactive-bg)',
                color: activeTab === 'MANUAL' ? 'var(--color-cyan)' : 'var(--text-muted)',
              }}
            >
              {tabCounts.MANUAL}
            </span>
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ position: 'relative', minWidth: '240px' }}>
          <Search
            size={14}
            style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }}
          />
          <input
            type="text"
            placeholder="搜索标的代码 / 股票名称 / 策略..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '7px 12px 7px 32px',
              borderRadius: '8px',
              border: '1px solid var(--border-subtle)',
              background: 'var(--bg-card)',
              color: 'var(--text-primary)',
              fontSize: '12px',
              outline: 'none',
              transition: 'border-color 0.2s ease',
            }}
          />
        </div>
      </div>

      {/* 3. Main Observability Grid Table */}
      <div
        className="watchlist-table-container"
        style={{
          background: 'var(--bg-card)',
          borderRadius: '12px',
          border: '1px solid var(--border-subtle)',
          boxShadow: 'var(--shadow-card)',
          overflow: 'hidden',
        }}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: 'var(--bg-canvas)', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
                <th style={{ padding: '12px 16px', fontWeight: 600 }}>标的代码 / 股票名称</th>
                <th style={{ padding: '12px 14px', fontWeight: 600 }}>来源 / 策略标签</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'right' }}>最新定K价</th>
                <th style={{ padding: '12px 14px', fontWeight: 600, textAlign: 'right' }}>最新涨跌幅</th>
                <th style={{ padding: '12px 14px', fontWeight: 600, textAlign: 'right' }}>观察参考价</th>
                <th style={{ padding: '12px 14px', fontWeight: 600, textAlign: 'right' }}>参考价偏离度</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'center' }}>高频分时走势</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'center' }}>最新定K时间 (UTC+8)</th>
                <th style={{ padding: '12px 14px', fontWeight: 600, textAlign: 'center' }}>切片状态</th>
                <th style={{ padding: '12px 16px', fontWeight: 600, textAlign: 'center' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    {loading ? '正在同步分时切片数据...' : '当前分类暂无匹配的自选股标的'}
                  </td>
                </tr>
              ) : (
                filteredItems.map((item) => {
                  const priceSign = item.change_pct ? getDecimalSign(item.change_pct.replace('%', '')) : 0;
                  const priceColor = getPriceColor(priceSign);

                  return (
                    <tr
                      key={item.symbol}
                      style={{
                        borderBottom: '1px solid var(--border-subtle)',
                        transition: 'background 0.15s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-surface-hover)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      {/* 1. Symbol / Name */}
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: '14px' }}>
                            {item.name}
                          </span>
                          <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                            {item.symbol}
                          </span>
                        </div>
                      </td>

                      {/* 2. Source / Tag */}
                      <td style={{ padding: '14px 14px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                          {item.source === 'DOCTOR_A' && (
                            <span
                              style={{
                                fontSize: '10.5px',
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: 'var(--badge-info-bg)',
                                color: 'var(--color-cyan)',
                                border: '1px solid var(--badge-info-border)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                              }}
                            >
                              <Bot size={11} />
                              Doctor-A
                            </span>
                          )}
                          {item.source === 'AMBUSH_RADAR' && (
                            <span
                              style={{
                                fontSize: '10.5px',
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: 'var(--badge-bullish-bg)',
                                color: 'var(--color-emerald)',
                                border: '1px solid var(--badge-bullish-border)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                              }}
                            >
                              <Crosshair size={11} />
                              潜伏雷达
                            </span>
                          )}
                          {item.source === 'MANUAL' && (
                            <span
                              style={{
                                fontSize: '10.5px',
                                fontWeight: 600,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                background: 'var(--badge-warning-bg)',
                                color: 'var(--color-amber)',
                                border: '1px solid var(--badge-warning-border)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '3px',
                              }}
                            >
                              <Star size={11} />
                              核心自选
                            </span>
                          )}
                          {item.strategy_tag && (
                            <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                              {item.strategy_tag}
                            </span>
                          )}
                        </div>
                      </td>

                      {/* 3. Last Closed Price */}
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <span
                          style={{
                            fontSize: '15px',
                            fontWeight: 700,
                            fontFamily: 'var(--font-mono)',
                            color: priceColor,
                          }}
                        >
                          {item.last_closed_price ? formatPrice(item.last_closed_price) : '--'}
                        </span>
                      </td>

                      {/* 4. Change % */}
                      <td style={{ padding: '14px 14px', textAlign: 'right' }}>
                        <span
                          style={{
                            fontSize: '12.5px',
                            fontWeight: 600,
                            fontFamily: 'var(--font-mono)',
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: priceSign >= 0 ? 'var(--badge-bearish-bg)' : 'var(--badge-bullish-bg)',
                            color: priceColor,
                          }}
                        >
                          {item.change_pct}
                        </span>
                      </td>

                      {/* 5. Reference Price */}
                      <td style={{ padding: '14px 14px', textAlign: 'right' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', fontSize: '13px' }}>
                          {item.reference_price ? formatPrice(item.reference_price) : '--'}
                        </span>
                      </td>

                      {/* 6. Reference Bias % */}
                      <td style={{ padding: '14px 14px', textAlign: 'right' }}>
                        {item.reference_bias_pct ? (
                          <span
                            style={{
                              fontSize: '12px',
                              fontFamily: 'var(--font-mono)',
                              fontWeight: 600,
                              color: item.reference_bias_pct.startsWith('+') ? 'var(--color-bearish)' : 'var(--color-bullish)',
                            }}
                          >
                            {item.reference_bias_pct}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>--</span>
                        )}
                      </td>

                      {/* 7. Sparkline */}
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        {renderSparkline(item.sparkline_1m, priceColor)}
                      </td>

                      {/* 8. Bar Close Time */}
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                          {formatUtcToCst(item.bar_close_time_utc)}
                        </span>
                      </td>

                      {/* 9. Status */}
                      <td style={{ padding: '14px 14px', textAlign: 'center' }}>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            padding: '3px 8px',
                            borderRadius: '4px',
                            background: 'var(--badge-bullish-bg)',
                            color: 'var(--color-emerald)',
                            border: '1px solid var(--badge-bullish-border)',
                          }}
                        >
                          已定K
                        </span>
                      </td>

                      {/* 10. Action Button */}
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <button
                          onClick={() => setSelectedStock(item)}
                          style={{
                            padding: '5px 10px',
                            borderRadius: '6px',
                            border: '1px solid var(--border-subtle)',
                            background: 'var(--bg-canvas)',
                            color: 'var(--color-cyan)',
                            fontSize: '11.5px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <BarChart2 size={12} />
                          分时K线
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Deep-Dive K-Line Slide-Over Drawer */}
      {selectedStock && (
        <div
          className="drawer-overlay"
          onClick={() => setSelectedStock(null)}
        >
          <div
            className="drawer-panel"
            style={{
              width: '680px',
              maxWidth: '90vw',
              height: '100%',
              background: 'var(--bg-card)',
              boxShadow: 'var(--shadow-drawer)',
              borderLeft: '1px solid var(--border-subtle)',
              display: 'flex',
              flexDirection: 'column',
              padding: '24px',
              gap: '16px',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border-subtle)', paddingBottom: '16px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
                    {selectedStock.name}
                  </h2>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                    {selectedStock.symbol}
                  </span>
                  <span
                    style={{
                      fontSize: '11px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      background: 'var(--badge-bullish-bg)',
                      color: 'var(--color-emerald)',
                      fontWeight: 600,
                    }}
                  >
                    已定K切片
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  高频分时切片历史明细 · 因果时延窗口 W ≥ 3.0s · 纯 Decimal 精度
                </div>
              </div>
              <button
                onClick={() => setSelectedStock(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: '4px',
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Timeframe Selector */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'inline-flex', background: 'var(--bg-canvas)', padding: '3px', borderRadius: '8px', gap: '3px' }}>
                {['1m', '5m', '15m', '60m'].map((tf) => (
                  <button
                    key={tf}
                    onClick={() => setSelectedTimeframe(tf)}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      fontSize: '11.5px',
                      fontWeight: selectedTimeframe === tf ? 600 : 500,
                      background: selectedTimeframe === tf ? 'var(--color-cyan)' : 'transparent',
                      color: selectedTimeframe === tf ? 'var(--text-inverse)' : 'var(--text-secondary)',
                      cursor: 'pointer',
                    }}
                  >
                    {tf === '1m' ? '1分钟 (1m)' : tf === '5m' ? '5分钟 (5m)' : tf === '15m' ? '15分钟 (15m)' : '60分钟 (60m)'}
                  </button>
                ))}
              </div>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                展示最近 {drawerCandles.length} 条闭合分时切片
              </span>
            </div>

            {/* Candlestick Table */}
            <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: '8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-canvas)', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}>
                    <th style={{ padding: '10px 12px' }}>K线时间 (UTC+8)</th>
                    <th style={{ padding: '10px 10px', textAlign: 'right' }}>开盘价</th>
                    <th style={{ padding: '10px 10px', textAlign: 'right' }}>最高价</th>
                    <th style={{ padding: '10px 10px', textAlign: 'right' }}>最低价</th>
                    <th style={{ padding: '10px 10px', textAlign: 'right' }}>收盘价</th>
                    <th style={{ padding: '10px 10px', textAlign: 'right' }}>成交量</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>成交额</th>
                  </tr>
                </thead>
                <tbody>
                  {drawerLoading ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                        正在加载分时切片...
                      </td>
                    </tr>
                  ) : drawerCandles.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '32px', textAlign: 'center', color: 'var(--text-muted)' }}>
                        暂无分时切片数据
                      </td>
                    </tr>
                  ) : (
                    drawerCandles.map((c) => {
                      const cmp = compareDecimalStrings(c.close, c.open);
                      const cColor =
                        cmp > 0
                          ? colorMode === 'mainland'
                            ? 'var(--color-bearish)'
                            : 'var(--color-bullish)'
                          : cmp < 0
                          ? colorMode === 'mainland'
                            ? 'var(--color-bullish)'
                            : 'var(--color-bearish)'
                          : 'var(--text-primary)';

                      return (
                        <tr key={c.timestamp} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                          <td style={{ padding: '8px 12px', fontFamily: 'var(--font-mono)' }}>
                            {new Date(c.timestamp).toLocaleString('zh-CN', { hour12: false })}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                            {formatPrice(c.open)}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                            {formatPrice(c.high)}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                            {formatPrice(c.low)}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 600, color: cColor }}>
                            {formatPrice(c.close)}
                          </td>
                          <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                            {formatVolume(c.volume, { abbreviate: 'zh' })}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                            {formatVolume(c.amount, { abbreviate: 'zh' })}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
